import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ScatterChart,
  Scatter,
  ZAxis,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useData } from "../lib/data-context";
import { fmtInt, fmtDec, fmtMWh, viabilidadColor } from "../lib/api";
import KPICard from "../components/KPICard";
import Semaforo from "../components/Semaforo";
import { Package, Activity, Zap, TrendingUp } from "lucide-react";

export default function Lotes() {
  const { lineas, lotes, loading } = useData();
  const [selectedLote, setSelectedLote] = useState(null);
  const nav = useNavigate();
  // Lote activo: el seleccionado por el usuario o, por defecto, el primero disponible.
  const lote = selectedLote && lotes.includes(selectedLote) ? selectedLote : lotes[0];

  const lineasLote = useMemo(
    () => (lineas || []).filter((l) => l.lote === lote),
    [lineas, lote]
  );

  const stats = useMemo(() => {
    if (!lineasLote.length) return null;
    const total = lineasLote.length;
    const consMedio = lineasLote.reduce((s, l) => s + l.consumo_kwh_km_base, 0) / total;
    const consAnual = lineasLote.reduce((s, l) => s + l.consumo_anual_estimado_kwh, 0);
    const vehiculos = lineasLote.reduce(
      (s, l) => s + Math.max(1, Math.floor(l.veh_km_anuales / 60000)),
      0
    );
    const porTamano = {};
    const porViab = { Alta: 0, Condicionada: 0, "A revisar": 0 };
    lineasLote.forEach((l) => {
      porTamano[l.tamano_vehiculo] = (porTamano[l.tamano_vehiculo] || 0) + 1;
      porViab[l.viabilidad] = (porViab[l.viabilidad] || 0) + 1;
    });
    return {
      total,
      consMedio,
      consAnual,
      vehiculos,
      porTamano: Object.entries(porTamano).map(([t, n]) => ({ tamano: t, n })),
      porViab: Object.entries(porViab).map(([v, n]) => ({ name: v, value: n })),
    };
  }, [lineasLote]);

  if (loading) return <div className="text-gray-500">Cargando…</div>;

  return (
    <div className="space-y-7" data-testid="lotes-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Vista por lote</div>
        <h1 className="section-title mt-1">Análisis individual del lote</h1>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="lote-tabs">
        {lotes.map((l) => {
          const active = l === lote;
          return (
            <button
              key={l}
              onClick={() => setSelectedLote(l)}
              data-testid={`lote-tab-${l}`}
              className={
                "px-4 py-2 rounded-lg text-sm font-semibold border transition-colors " +
                (active
                  ? "bg-[#14532D] text-white border-[#14532D]"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-300")
              }
            >
              {l}
            </button>
          );
        })}
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <KPICard testId="lote-kpi-lineas" label={`Líneas ${lote}`} value={stats.total} icon={Activity} tone="blue" />
            <KPICard testId="lote-kpi-veh" label="Vehículos estim." value={stats.vehiculos} icon={Package} />
            <KPICard
              testId="lote-kpi-medio"
              label="Consumo medio"
              value={`${fmtDec(stats.consMedio, 2)} kWh/km`}
              icon={Zap}
              tone="green"
            />
            <KPICard
              testId="lote-kpi-anual"
              label="Consumo anual"
              value={fmtMWh(stats.consAnual)}
              icon={TrendingUp}
              tone="red"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="crtm-card p-5">
              <h3 className="font-heading font-semibold text-[15px] mb-3">Vehículos por tamaño</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.porTamano}>
                  <CartesianGrid stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="tamano" tick={{ fontSize: 12, fill: "#4B5563" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="n" fill="#14532D" radius={[4, 4, 0, 0]} maxBarSize={50} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="crtm-card p-5">
              <h3 className="font-heading font-semibold text-[15px] mb-3">Viabilidad eléctrica</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.porViab}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={88}
                    innerRadius={55}
                  >
                    {stats.porViab.map((d, i) => (
                      <Cell key={i} fill={viabilidadColor(d.name)} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "#4B5563" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="crtm-card p-5">
            <h3 className="font-heading font-semibold text-[15px] mb-3">Consumo kWh/km por línea</h3>
            <ResponsiveContainer width="100%" height={Math.max(280, lineasLote.length * 22)}>
              <BarChart
                data={[...lineasLote].sort((a, b) => b.consumo_kwh_km_base - a.consumo_kwh_km_base)}
                layout="vertical"
                margin={{ left: 50 }}
              >
                <CartesianGrid stroke="#F3F4F6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                <YAxis
                  dataKey="numero_linea"
                  type="category"
                  tick={{ fontSize: 11, fill: "#4B5563" }}
                  width={60}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="consumo_kwh_km_base" radius={[0, 3, 3, 0]} maxBarSize={14}>
                  {lineasLote.map((l, i) => (
                    <Cell key={i} fill={viabilidadColor(l.viabilidad)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="crtm-card p-5">
              <h3 className="font-heading font-semibold text-[15px] mb-3">Longitud línea vs Factor kWh/km</h3>
              <ResponsiveContainer width="100%" height={300}>
                <ScatterChart>
                  <CartesianGrid stroke="#F3F4F6" />
                  <XAxis
                    type="number"
                    dataKey="longitud_km"
                    name="Longitud"
                    unit=" km"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    type="number"
                    dataKey="consumo_kwh_km_base"
                    name="Factor"
                    unit=" kWh/km"
                    tick={{ fontSize: 11 }}
                  />
                  <ZAxis range={[60, 200]} />
                  <Tooltip cursor={{ strokeDasharray: "3 3" }} contentStyle={tooltipStyle} />
                  <Scatter data={lineasLote} fill="#14532D">
                    {lineasLote.map((l, i) => (
                      <Cell key={i} fill={viabilidadColor(l.viabilidad)} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            <div className="crtm-card p-5">
              <h3 className="font-heading font-semibold text-[15px] mb-3">Consumo anual por línea</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={[...lineasLote].sort((a, b) => b.consumo_anual_estimado_kwh - a.consumo_anual_estimado_kwh).slice(0, 15)}
                >
                  <CartesianGrid stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="numero_linea" tick={{ fontSize: 10, fill: "#4B5563" }} angle={-30} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtInt(v) + " kWh"} />
                  <Bar dataKey="consumo_anual_estimado_kwh" fill="#991B1B" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tabla líneas del lote */}
          <div className="crtm-card" data-testid="tabla-lineas-lote">
            <div className="p-5 border-b border-gray-100">
              <h3 className="font-heading font-semibold text-[15px]">Líneas del lote {lote}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="crtm-table">
                <thead>
                  <tr>
                    <th>Línea</th>
                    <th>Denominación</th>
                    <th>Tipo</th>
                    <th>Tipología</th>
                    <th>Long. (km)</th>
                    <th>kWh/km base</th>
                    <th>Consumo anual</th>
                    <th>Viabilidad</th>
                  </tr>
                </thead>
                <tbody>
                  {lineasLote.map((l) => (
                    <tr
                      key={l.codigo_linea}
                      className="clickable"
                      onClick={() => nav(`/linea/${encodeURIComponent(l.codigo_linea)}`)}
                    >
                      <td className="font-semibold">{l.numero_linea}</td>
                      <td className="text-gray-700">{l.denominacion}</td>
                      <td><span className="pill neutral">{l.tipo_servicio}</span></td>
                      <td className="text-xs">{l.tipologia_vehiculo}</td>
                      <td className="font-mono">{fmtDec(l.longitud_km, 2)}</td>
                      <td className="font-mono">{fmtDec(l.consumo_kwh_km_base, 2)}</td>
                      <td className="font-mono">{fmtMWh(l.consumo_anual_estimado_kwh)}</td>
                      <td><Semaforo viabilidad={l.viabilidad} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const tooltipStyle = {
  background: "white",
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  fontSize: 12,
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};
